import React, { useState } from "react";
import { ChevronDown, ChevronUp, FileText, Shield, CreditCard, HardDrive, AlertTriangle, Scale, Ban } from "lucide-react";
import SEO from "../components/SEO";

const TermsConditions = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (index) => {
    setExpandedSection(expandedSection === index ? null : index);
  };

  const sections = [
    {
      icon: FileText,
      title: "1. Acceptance of Terms",
      content: "By accessing and using ReferMe, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.",
      importance: "high"
    },
    {
      icon: FileText,
      title: "2. Use License",
      content: "Permission is granted to temporarily download one copy of the materials (information or software) on ReferMe for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title.",
      importance: "medium"
    },
    {
      icon: Shield,
      title: "3. User Responsibilities",
      content: "Users are responsible for the content they upload and must ensure it is accurate, appropriate, and does not violate any copyright or intellectual property rights. Users must not upload malicious content or attempt to harm the platform.",
      importance: "high"
    },
    {
      icon: CreditCard,
      title: "4. Payment and Purchases",
      content: "All payments on ReferMe are processed through Razorpay, a third-party payment gateway. By making a purchase, you agree to Razorpay's terms and conditions. We do not store your payment card details. All transactions are subject to verification and may be declined for security reasons. Prices are subject to change without notice.",
      importance: "high"
    },
    {
      icon: CreditCard,
      title: "5. Refunds and Cancellations",
      content: "ReferMe generally does not offer refunds because purchased materials are digital and access is granted immediately. In exceptional circumstances — such as duplicate purchases, demonstrable technical errors that prevented access, or content that is materially different from its description — we may consider a refund on a case-by-case basis. To request a review, contact us at craftingwithpixen@gmail.com within 7 days of purchase and include your order ID and supporting details. If a refund is approved, it will be processed via Razorpay and may take several business days to appear in your account. All refund decisions are at ReferMe's sole discretion.",
      importance: "high"
    },
    {
      icon: HardDrive,
      title: "6. Content Delivery via Google Drive",
      content: "Study materials purchased on ReferMe are stored and delivered through Google Drive. By using our services, you acknowledge that your purchased content will be shared with you via Google Drive links. We are not responsible for Google Drive service interruptions, file accessibility issues, or changes to Google's policies. You are responsible for downloading and backing up your purchased materials.",
      importance: "medium"
    },
    {
      icon: AlertTriangle,
      title: "6. Content Guidelines",
      content: "All uploaded content must be educational in nature, relevant to engineering studies, and comply with academic standards. Inappropriate, offensive, or copyrighted content will be removed.",
      importance: "high"
    },
    {
      icon: Shield,
      title: "7. Privacy and Data",
      content: "We respect your privacy and are committed to protecting your personal information. Please refer to our Privacy Policy for detailed information about how we collect, use, and protect your data. By using ReferMe, you consent to sharing necessary information with Razorpay for payment processing and Google Drive for content delivery.",
      importance: "high"
    },
    {
      icon: FileText,
      title: "8. Intellectual Property",
      content: "The content on ReferMe is provided for educational purposes. Users retain rights to their original content, but grant ReferMe a license to host and distribute their materials for educational use. Purchased materials are licensed to you for personal use only and may not be redistributed, resold, or shared.",
      importance: "medium"
    },
    {
      icon: HardDrive,
      title: "9. Third-Party Services",
      content: "ReferMe uses third-party services including Razorpay for payment processing and Google Drive for content storage and delivery. These services operate under their own terms and privacy policies. We recommend reviewing Razorpay's Terms of Service and Google's Terms of Service. ReferMe is not responsible for the actions, policies, or service quality of these third-party providers.",
      importance: "medium"
    },
    {
      icon: Scale,
      title: "10. Limitation of Liability",
      content: "ReferMe shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses. We are not liable for payment processing errors, delays, or failures caused by Razorpay, or for content delivery issues caused by Google Drive.",
      importance: "high"
    },
    {
      icon: Ban,
      title: "11. Termination",
      content: "We may terminate or suspend your access immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms. Upon termination, you will lose access to any materials stored on our platform, but will retain access to previously purchased content via Google Drive.",
      importance: "medium"
    }
  ];

  const restrictions = [
    "Uploading copyrighted material without permission",
    "Sharing inappropriate or offensive content",
    "Attempting to hack or disrupt the platform",
    "Using the platform for commercial purposes",
    "Violating any applicable laws or regulations",
    "Impersonating others or providing false information",
    "Sharing purchased content with others or reselling materials",
    "Making fraudulent payment claims or chargebacks without valid reason"
  ];

  const permissions = [
    "Create and manage a personal account and profile",
    "Browse and search branches, subjects, and units",
    "Purchase study materials and access purchased content",
    "Preview available demo or free content",
    "Provide feedback, rate content, and contact support",
    "Receive transactional emails and in-app notifications related to purchases and account activity",
    "Use the platform for academic and non-commercial purposes only"
  ];

  return (
    <>
      <SEO 
        title="Terms & Conditions - ReferMe"
        description="Read ReferMe's terms and conditions for using our educational platform, purchasing study materials, and understanding your rights and responsibilities."
        url="/terms"
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 py-16 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-2xl mb-6 shadow-lg shadow-indigo-500/30">
            <Scale className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Terms & Conditions
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-3">
            Please read these terms carefully before using ReferMe
          </p>
          <div className="inline-flex items-center gap-2 text-sm text-gray-500">
            <FileText className="w-4 h-4" />
            <span>Last updated: November 9, 2025</span>
          </div>
        </div>

        {/* Important Notice Banner */}
        <div className="mb-10">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 p-6">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-orange-500/5 backdrop-blur-sm"></div>
            <div className="relative flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-amber-300 mb-2">Important Legal Agreement</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  By accessing or using ReferMe, you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions. If you do not agree to these terms, please do not use our services.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="mb-10">
          <div className="bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 backdrop-blur-lg rounded-2xl p-6 border border-gray-800/50 shadow-xl">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-400" />
              Table of Contents
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {sections.map((section, index) => (
                <button
                  key={index}
                  onClick={() => {
                    const element = document.getElementById(`section-${index}`);
                    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="text-left px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-indigo-400 hover:bg-gray-800/50 transition-all duration-200"
                >
                  {section.title}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Terms Sections - Accordion Style */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-white mb-6">Detailed Terms</h2>
          <div className="space-y-3">
            {sections.map((section, index) => {
              const Icon = section.icon;
              const isExpanded = expandedSection === index;
              const importanceColors = {
                high: "border-red-500/30 bg-red-500/5",
                medium: "border-gray-700/50 bg-gray-800/30",
              };
              
              return (
                <div
                  key={index}
                  id={`section-${index}`}
                  className={`bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 backdrop-blur-lg rounded-xl border ${
                    importanceColors[section.importance]
                  } overflow-hidden transition-all duration-300 hover:border-indigo-500/30`}
                >
                  <button
                    onClick={() => toggleSection(index)}
                    className="w-full px-6 py-4 flex items-center justify-between gap-4 text-left hover:bg-gray-800/30 transition-all duration-200"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${
                        section.importance === 'high' 
                          ? 'bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600' 
                          : 'bg-gray-800'
                      } flex items-center justify-center shadow-lg`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-white mb-1">
                          {section.title}
                        </h3>
                        {section.importance === 'high' && (
                          <span className="inline-flex items-center text-xs text-amber-400">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Important
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div className="px-6 pb-6 pt-2">
                      <div className="pl-14">
                        <p className="text-gray-300 leading-relaxed text-[15px]">
                          {section.content}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Permissions and Restrictions */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-white mb-6">User Guidelines</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Permitted Activities */}
            <div className="bg-gradient-to-br from-green-900/20 via-green-900/10 to-gray-900/20 backdrop-blur-lg rounded-xl p-6 border border-green-500/20 shadow-xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-green-400">
                  Permitted Activities
                </h3>
              </div>
              <ul className="space-y-3">
                {permissions.map((permission, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-300">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center mt-0.5">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    </div>
                    <span className="text-sm leading-relaxed">{permission}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Prohibited Activities */}
            <div className="bg-gradient-to-br from-red-900/20 via-red-900/10 to-gray-900/20 backdrop-blur-lg rounded-xl p-6 border border-red-500/20 shadow-xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Ban className="w-5 h-5 text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-red-400">
                  Prohibited Activities
                </h3>
              </div>
              <ul className="space-y-3">
                {restrictions.map((restriction, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-300">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center mt-0.5">
                      <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                    </div>
                    <span className="text-sm leading-relaxed">{restriction}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Legal Information */}
        

        {/* Updates and Changes */}
        <div className="mb-10">
          <div className="bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 backdrop-blur-lg rounded-xl p-6 border border-gray-800/50 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 flex items-center justify-center shadow-lg">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">
                Changes to Terms
              </h3>
            </div>
            <div className="space-y-3 text-gray-300 text-sm leading-relaxed">
              <p>
                We reserve the right to modify these terms at any time. We will notify users of any material changes by posting the new terms on this page and updating the "Last updated" date.
              </p>
              <p>
                Your continued use of ReferMe after any changes constitutes acceptance of the new terms.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="mb-10">
          <div className="bg-gradient-to-br from-indigo-900/20 via-purple-900/20 to-pink-900/20 backdrop-blur-lg rounded-xl p-6 border border-indigo-500/20 shadow-xl">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 flex items-center justify-center shadow-lg">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Questions About These Terms?
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed mb-3">
                  If you have any questions or concerns about these Terms & Conditions, please contact us:
                </p>
                <div className="space-y-1.5">
                  <p className="text-indigo-400 text-sm">
                    <strong>Email:</strong> craftingwithpixen@gmail.com
                  </p>
                  <p className="text-gray-400 text-sm">
                    <strong>Response Time:</strong> Within 48 hours
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center pt-6 border-t border-gray-800">
          <p className="text-gray-500 text-xs">
            By using ReferMe, you acknowledge that you have read and understood these Terms & Conditions
          </p>
        </div>

      </div>
    </div>
    </>
  );
};

export default TermsConditions; 